/* eslint-disable @angular-eslint/component-selector */
import { Component, Input, PipeTransform, ChangeDetectorRef, ElementRef, ViewContainerRef, OnDestroy, DoCheck, inject, input, output, viewChild } from '@angular/core';

import { NgTemplateOutlet } from '@angular/common';
import { TableColumn } from '../../types/table-column.type';
import { SortDirection } from '../../types/sort-direction.type';
import { SortPropDir } from '../../types/sort-prop-dir.type';
import { Keys } from '../../utils/keys';
import { HtmlSanitizerService } from '../../../../utils/sanitization/strip-html-helper';

export type TreeStatus = 'collapsed' | 'expanded' | 'loading' | 'disabled';

interface CellContext {
  onCheckboxChangeFn: (event: unknown) => void;
  activateFn: (event: Record<string, unknown>) => void;
  row: unknown;
  group: unknown;
  value: unknown;
  column: TableColumn;
  rowHeight: number | 'auto';
  isSelected: boolean;
  rowIndex: number;
  treeStatus: TreeStatus;
  expanded?: boolean;
  onTreeAction: () => void;
}

@Component({
  selector: 'datatable-body-cell',
  template: `
      <div class="datatable-body-cell-label"
           [style.width.%]="100"
           [style.margin-left.px]="calcLeftMargin(column, row)">
        @if (column?.checkboxable && (!displayCheck() || displayCheck()(row, column, value))) {
          <label class="datatable-checkbox">
            <input type="checkbox"
                   [checked]="isSelected"
                   (click)="onCheckboxChange($event)"/>
          </label>
        }
        @if (column?.isTreeColumn) {
          @if (!column?.treeToggleTemplate) {
            <button class="datatable-tree-button"
                    [disabled]="treeStatus === 'disabled'"
                    (click)="onTreeAction()">
          <span>
            @if (treeStatus === 'loading') {
              <i class="icon datatable-icon-collapse"></i>
            }
            @if (treeStatus === 'collapsed') {
              <i class="icon datatable-icon-up"></i>
            }
            @if (treeStatus === 'expanded' || treeStatus === 'disabled') {
              <i class="icon datatable-icon-down"></i>
            }
          </span>
            </button>
          }
          @if (column?.treeToggleTemplate) {
            <ng-template [ngTemplateOutlet]="column?.treeToggleTemplate"
                         [ngTemplateOutletContext]="{ cellContext: cellContext }" />
          }
        }

        @if (!column?.cellTemplate) {
          <span [title]="sanitizedValue"
                [innerHTML]="value"> </span>
        }
        @if (column?.cellTemplate) {
          <ng-template #cellTemplate
                       [ngTemplateOutlet]="column?.cellTemplate"
                       [ngTemplateOutletContext]="cellContext" />
        }
      </div>
    `,
  imports: [NgTemplateOutlet],
  host: {
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '(click)': 'onClick($event)',
    '(dblclick)': 'onDblClick($event)',
    '(keydown)': 'onKeyDown($event)',
    '[class]': 'columnCssClasses',
    '[style.width.px]': '(column ? column.width : undefined)',
    '[style.minWidth.px]': '(column ? column.minWidth : undefined)',
    '[style.maxWidth.px]': '(column ? column.maxWidth : undefined)',
    '[style.height]': 'height'
  }
})
export class DataTableBodyCellComponent implements DoCheck, OnDestroy {
  private readonly cd = inject(ChangeDetectorRef);
  private readonly htmlSanitizer = inject(HtmlSanitizerService);

  readonly displayCheck = input<(row: unknown, column?: TableColumn, value?: unknown) => boolean>(undefined);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set group(group: unknown) {
    this._group = group;
    this.cellContext.group = group;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get group(): unknown {
    return this._group;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set rowHeight(val: number | 'auto') {
    this._rowHeight = val;
    this.cellContext.rowHeight = val;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get rowHeight(): number | 'auto' {
    return this._rowHeight;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set isSelected(val: boolean) {
    this._isSelected = val;
    this.cellContext.isSelected = val;
    this.cd.markForCheck();
  }

  get isSelected(): boolean {
    return this._isSelected;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set expanded(val: boolean) {
    this._expanded = val;
    this.cellContext.expanded = val;
    this.cd.markForCheck();
  }

  get expanded(): boolean {
    return this._expanded;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set rowIndex(val: number) {
    this._rowIndex = val;
    this.cellContext.rowIndex = val;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get rowIndex(): number {
    return this._rowIndex;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set column(column: TableColumn) {
    this._column = column;
    this.cellContext.column = column;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get column(): TableColumn {
    return this._column;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set row(row: unknown) {
    this._row = row;
    this.cellContext.row = row;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get row(): unknown {
    return this._row;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set sorts(val: Array<SortPropDir>) {
    this._sorts = val;
    this.sortDir = this.calcSortDir(val);
  }

  get sorts(): Array<SortPropDir> {
    return this._sorts;
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set treeStatus(status: TreeStatus) {
    if (status !== 'collapsed' && status !== 'expanded' && status !== 'loading' && status !== 'disabled') {
      this._treeStatus = 'collapsed';
    } else {
      this._treeStatus = status;
    }
    this.cellContext.treeStatus = this._treeStatus;
    this.checkValueUpdates();
    this.cd.markForCheck();
  }

  get treeStatus(): TreeStatus {
    return this._treeStatus;
  }

  readonly activate = output<Record<string, unknown>>();

  readonly treeAction = output<unknown>();

  readonly cellTemplate = viewChild('cellTemplate', { read: ViewContainerRef });

  get columnCssClasses(): unknown {
    let cls = 'datatable-body-cell';
    // Guard against undefined column during early change detection in tests
    if (this.column && this.column.cellClass) {
      if (typeof this.column.cellClass === 'string') {
        cls += ' ' + this.column.cellClass;
      } else if (typeof this.column.cellClass === 'function') {
        const res = this.column.cellClass({
          row: this.row,
          group: this.group,
          column: this.column,
          value: this.value,
          rowHeight: this.rowHeight
        });

        if (typeof res === 'string') {
          cls += ' ' + res;
        } else if (typeof res === 'object') {
          const keys = Object.keys(res);
          for (const k of keys) {
            if (res[k] === true) {
              cls += ` ${k}`;
            }
          }
        }
      }
    }
    if (!this.sortDir) {
      cls += ' sort-active';
    }
    if (this.isFocused) {
      cls += ' active';
    }
    if (this.sortDir === SortDirection.ASC) {
      cls += ' sort-asc';
    }
    if (this.sortDir === SortDirection.DESC) {
      cls += ' sort-desc';
    }
    return cls;
  }

  get height(): string | number {
    const height = this.rowHeight;
    if (isNaN(height as number)) {
      return height;
    }
    return height + 'px';
  }

  sanitizedValue: unknown;
  value: unknown;
  sortDir: SortDirection;
  isFocused = false;
  onCheckboxChangeFn = this.onCheckboxChange.bind(this);
  activateFn = this.activate.emit.bind(this.activate);

  cellContext: CellContext;

  private _isSelected: boolean;
  private _sorts: Array<SortPropDir>;
  private _column: TableColumn;
  private _row: unknown;
  private _group: unknown;
  private _rowHeight: number | 'auto';
  private _rowIndex: number;
  private _expanded: boolean;
  private readonly _element: unknown;
  private _treeStatus: TreeStatus;

  constructor() {
    const element = inject(ElementRef);

    this.cellContext = {
      onCheckboxChangeFn: this.onCheckboxChangeFn,
      activateFn: this.activateFn,
      row: this.row,
      group: this.group,
      value: this.value,
      column: this.column,
      rowHeight: this.rowHeight,
      isSelected: this.isSelected,
      rowIndex: this.rowIndex,
      treeStatus: this.treeStatus,
      onTreeAction: this.onTreeAction.bind(this)
    };

    this._element = element.nativeElement;
  }

  ngDoCheck(): void {
    this.checkValueUpdates();
  }

  ngOnDestroy(): void {
    const cellTemplate = this.cellTemplate();
    if (cellTemplate) {
      cellTemplate.clear();
    }
  }

  checkValueUpdates(): void {
    let value: string;

    if (!this.row || !this.column) {
      value = '';
    } else {
      const val = this.column.$$valueGetter(this.row, this.column.prop);
      const userPipe: PipeTransform = this.column.pipe;

      if (userPipe) {
        value = userPipe.transform(val);
      } else {
        value = val as string;
      }
    }

    if (this.value !== value) {
      this.value = value;
      this.cellContext.value = value;
      this.sanitizedValue = value?.length ? this.stripHtml(value) : value;
      this.cd.markForCheck();
    }
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
  }

  onClick(event: MouseEvent): void {
    this.activate.emit({
      type: 'click',
      event,
      row: this.row,
      group: this.group,
      rowHeight: this.rowHeight,
      column: this.column,
      value: this.value,
      cellElement: this._element
    });
  }

  onDblClick(event: MouseEvent): void {
    this.activate.emit({
      type: 'dblclick',
      event,
      row: this.row,
      group: this.group,
      rowHeight: this.rowHeight,
      column: this.column,
      value: this.value,
      cellElement: this._element
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;
    const isTargetCell = event.target === this._element;

    const isAction =
      keyCode === Keys.return ||
      keyCode === Keys.down ||
      keyCode === Keys.up ||
      keyCode === Keys.left ||
      keyCode === Keys.right;

    if (isAction && isTargetCell) {
      event.preventDefault();
      event.stopPropagation();

      this.activate.emit({
        type: 'keydown',
        event,
        row: this.row,
        group: this.group,
        rowHeight: this.rowHeight,
        column: this.column,
        value: this.value,
        cellElement: this._element
      });
    }
  }

  onCheckboxChange(event: unknown): void {
    this.activate.emit({
      type: 'checkbox',
      event,
      row: this.row,
      group: this.group,
      rowHeight: this.rowHeight,
      column: this.column,
      value: this.value,
      cellElement: this._element,
      treeStatus: 'collapsed'
    });
  }

  calcSortDir(sorts: Array<SortPropDir>): SortDirection | undefined {
    if (!sorts) {
      return;
    }

    const sort = sorts.find((s: SortPropDir) => s.prop === this.column.prop);

    if (sort) {
      return sort.dir;
    }
  }

  stripHtml(html: string): string {
    if (!html.replace) {
      return html;
    }
    return this.htmlSanitizer.stripHtml(html);
  }

  onTreeAction(): void {
    this.treeAction.emit(this.row);
  }

  calcLeftMargin(column: TableColumn, row: unknown): number {
    // Safely handle cases where inputs are not yet provided (e.g., in tests)
    if (!column || !row) {
      return 0;
    }
    const levelIndent = column.treeLevelIndent == null ? 50 : column.treeLevelIndent;
    const rowRecord = row as Record<string, unknown>;
    const level = rowRecord['level'] == null ? 0 : rowRecord['level'] as number;
    return column.isTreeColumn ? level * levelIndent : 0;
  }
}
